/**
 * ManpowerRequestSettings.js
 * Settings component for HR to configure manpower request form
 * Uses toggle switches matching the approval workflow design
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ref, get, update } from 'firebase/database';
import { database } from '../../firebase';
import FormFieldsModal from './FormFieldsModal';

const ManpowerRequestSettings = ({ companyUID, companyName, isRTL = false }) => {
    const { t } = useTranslation('profile');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showFieldsModal, setShowFieldsModal] = useState(false);

    // Settings state
    const [enabled, setEnabled] = useState(false);
    const [requireInternalApproval, setRequireInternalApproval] = useState(false);
    const [companySlug, setCompanySlug] = useState('');
    const [allowedDomains, setAllowedDomains] = useState([]);
    const [emailWhitelist, setEmailWhitelist] = useState([]);
    const [approverEmails, setApproverEmails] = useState([]);

    // Temporary input states
    const [newDomain, setNewDomain] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newApprover, setNewApprover] = useState('');

    // Load settings on mount
    useEffect(() => {
        const loadSettings = async () => {
            if (!companyUID) return;
            try {
                const settingsRef = ref(database, `companies/${companyUID}/settings/manpowerRequest`);
                const snapshot = await get(settingsRef);
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setEnabled(data.enabled === true);
                    setRequireInternalApproval(data.requireInternalApproval === true);
                    setCompanySlug(data.companySlug || '');
                    setAllowedDomains(data.allowedDomains || []);
                    setEmailWhitelist(data.emailWhitelist || []);
                    setApproverEmails(data.approverEmails || []);
                } else {
                    // Generate default slug from company name
                    const defaultSlug = (companyName || 'company')
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-|-$/g, '');
                    setCompanySlug(defaultSlug);
                }
            } catch (err) {
                console.error('Error loading manpower request settings:', err);
            } finally {
                setLoading(false);
            }
        };
        loadSettings();
    }, [companyUID, companyName]);

    // Toggle handlers with auto-save
    const handleToggleEnabled = async () => {
        const newValue = !enabled;

        setEnabled(newValue);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                enabled: newValue,
                companySlug: companySlug.trim().toLowerCase() || generateSlug(companyName)
            });
        } catch (err) {
            console.error('Error updating settings:', err);
            setEnabled(!newValue); // Revert
        }
    };

    const handleToggleInternalApproval = async () => {
        const newValue = !requireInternalApproval;
        setRequireInternalApproval(newValue);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                requireInternalApproval: newValue
            });
        } catch (err) {
            console.error('Error updating settings:', err);
            setRequireInternalApproval(!newValue); // Revert
        }
    };

    // Helper to generate slug
    const generateSlug = (name) => {
        return (name || 'company')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    };

    // Save slug
    const handleSaveSlug = async () => {
        if (!companySlug.trim()) {
            alert(t('manpower.alertSlugRequired', 'Please enter a URL slug.'));
            return;
        }
        setSaving(true);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                companySlug: companySlug.trim().toLowerCase()
            });
        } catch (err) {
            console.error('Error saving slug:', err);
            alert(t('manpower.alertSaveFailed', 'Failed to save.'));
        } finally {
            setSaving(false);
        }
    };

    // Add/remove domain
    const addDomain = async () => {
        const value = newDomain.trim().toLowerCase();
        if (!value) return;
        if (allowedDomains.includes(value)) {
            alert(t('manpower.alertDomainExists', 'This domain already exists.'));
            return;
        }
        const newList = [...allowedDomains, value];
        setAllowedDomains(newList);
        setNewDomain('');
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                allowedDomains: newList
            });
        } catch (err) {
            console.error('Error adding domain:', err);
        }
    };

    const removeDomain = async (index) => {
        const newList = [...allowedDomains];
        newList.splice(index, 1);
        setAllowedDomains(newList);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                allowedDomains: newList
            });
        } catch (err) {
            console.error('Error removing domain:', err);
        }
    };

    // Add/remove whitelist email
    const addEmail = async () => {
        const value = newEmail.trim().toLowerCase();
        if (!value) return;
        if (emailWhitelist.includes(value)) {
            alert(t('manpower.alertEmailExists', 'This email already exists.'));
            return;
        }
        const newList = [...emailWhitelist, value];
        setEmailWhitelist(newList);
        setNewEmail('');
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                emailWhitelist: newList
            });
        } catch (err) {
            console.error('Error adding email:', err);
        }
    };

    const removeEmail = async (index) => {
        const newList = [...emailWhitelist];
        newList.splice(index, 1);
        setEmailWhitelist(newList);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                emailWhitelist: newList
            });
        } catch (err) {
            console.error('Error removing email:', err);
        }
    };

    // Add/remove approver emails (Finance/GM who approve requests)
    const addApprover = async () => {
        const value = newApprover.trim().toLowerCase();
        if (!value) return;
        if (!value.includes('@')) {
            alert(t('manpower.alertInvalidEmail', 'Please enter a valid email address.'));
            return;
        }
        if (approverEmails.includes(value)) {
            alert(t('manpower.alertApproverExists', 'This approver email already exists.'));
            return;
        }
        const newList = [...approverEmails, value];
        setApproverEmails(newList);
        setNewApprover('');
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                approverEmails: newList
            });
        } catch (err) {
            console.error('Error adding approver:', err);
        }
    };

    const removeApprover = async (index) => {
        const newList = [...approverEmails];
        newList.splice(index, 1);
        setApproverEmails(newList);
        try {
            await update(ref(database, `companies/${companyUID}/settings/manpowerRequest`), {
                approverEmails: newList
            });
        } catch (err) {
            console.error('Error removing approver:', err);
        }
    };

    // Shareable URL preview
    const shareableUrl = companySlug
        ? `${window.location.origin}/request-hire/${companySlug}`
        : '';

    // Toggle button component (matches approval workflow style)
    const ToggleButton = ({ isOn, onClick }) => (
        <button
            onClick={onClick}
            style={{ '--light-control-fill': isOn ? '#568ded' : 'var(--app-surface, #384860)',
                width: '50px',
                height: '26px',
                borderRadius: '13px',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                backgroundColor: isOn ? '#568ded' : 'var(--app-surface, #384860)',
                transition: 'background-color 0.2s',
                position: 'relative'
            }}
          data-light-control={isOn ? "filled" : "quiet"}>
            <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                backgroundColor: '#FFFFFF',
                position: 'absolute',
                top: '2px',
                left: isOn ? '26px' : '2px',
                transition: 'left 0.2s'
            }} />
        </button>
    );

    if (loading) {
        return (
            <div style={{ padding: '16px 0', textAlign: 'center' }}>
                <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '20px', color: '#568ded' }} />
            </div>
        );
    }

    return (
        <>
            <div style={{ marginTop: '20px' }}>
                {/* Enable Toggle */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                        <span style={{ fontFamily: 'Montserrat', color: 'var(--app-text, #FFFFFF)', fontSize: '14px', fontWeight: 600 }}>
                            {t('manpower.enableLabel', 'Enable manpower request form')}
                        </span>
                        <p style={{ fontFamily: 'Montserrat', color: 'var(--app-text, #FFFFFF)', opacity: 0.6, fontSize: '12px', marginTop: '4px' }}>
                            {t('manpower.enableDesc', 'Allow department managers to submit hiring requests via a public form.')}
                        </p>
                    </div>
                    <ToggleButton isOn={enabled} onClick={handleToggleEnabled} />
                </div>

                {/* Show remaining settings only when enabled */}
                {enabled && (
                    <>
                        {/* Internal Approval Toggle */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <div>
                                <span style={{ fontFamily: 'Montserrat', color: 'var(--app-text, #FFFFFF)', fontSize: '14px', fontWeight: 600 }}>
                                    {t('manpower.approvalLabel', 'Require Finance/GM approval')}
                                </span>
                                <p style={{ fontFamily: 'Montserrat', color: 'var(--app-text, #FFFFFF)', opacity: 0.6, fontSize: '12px', marginTop: '4px' }}>
                                    {t('manpower.approvalDesc', 'Requests must be approved by a manager before reaching HR.')}
                                </p>
                            </div>
                            <ToggleButton isOn={requireInternalApproval} onClick={handleToggleInternalApproval} />
                        </div>

                        {/* Customize Form Fields Button */}
                        <div style={{ marginBottom: '24px' }}>
                            <button
                                onClick={() => setShowFieldsModal(true)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px 16px',
                                    backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.15))',
                                    border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.4))",
                                    borderRadius: '8px',
                                    color: 'var(--app-text, #FFFFFF)',
                                    fontFamily: 'Montserrat, sans-serif',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    width: '100%',
                                    justifyContent: 'center'
                                }}
                              data-light-control={"quiet"}>
                                <i className="fa-solid fa-sliders" />
                                {t('manpower.customizeFields', 'Customize Form Fields')}
                            </button>
                        </div>

                        {/* URL Slug */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)',
                                marginBottom: '8px',
                                textTransform: 'none',
                                letterSpacing: '0.5px'
                            }}>
                                {t('manpower.urlSlug', 'Form URL Slug')}
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="text"
                                    value={companySlug}
                                    onChange={(e) => setCompanySlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                    placeholder={t('manpower.urlSlug', 'your-company-name')}
                                    className="manpower-settings-input"
                                    style={{
                                        flex: 1,
                                        padding: '10px 12px',
                                        backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))',
                                        border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                                        borderRadius: '8px',
                                        color: 'var(--app-text, #FFFFFF)',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                />
                                <button
                                    onClick={handleSaveSlug}
                                    disabled={saving}
                                    style={{ '--light-control-fill': '#568ded',
                                        padding: '0 16px',
                                        backgroundColor: '#568ded',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#FFFFFF',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        opacity: saving ? 0.6 : 1,
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                  data-light-control={"filled"}>
                                    {saving ? '...' : t('manpower.save', 'Save')}
                                </button>
                            </div>
                            {shareableUrl && (
                                <p style={{
                                    marginTop: '8px',
                                    fontSize: '11px',
                                    color: '#568ded',
                                    wordBreak: 'break-all'
                                }}>
                                    <i className="fa-solid fa-link" style={{ marginRight: '6px' }} />
                                    {shareableUrl}
                                </p>
                            )}
                        </div>

                        {/* Allowed Domains */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)',
                                marginBottom: '8px',
                                textTransform: 'none',
                                letterSpacing: '0.5px'
                            }}>
                                {t('manpower.allowedDomains', 'Allowed Email Domains')}
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                <input
                                    type="text"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ''))}
                                    placeholder={t('manpower.domainPlaceholder', 'Enter domain (e.g., company.com)')}
                                    className="manpower-settings-input"
                                    style={{
                                        flex: 1,
                                        padding: '10px 12px',
                                        backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))',
                                        border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                                        borderRadius: '8px',
                                        color: 'var(--app-text, #FFFFFF)',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                    onKeyPress={(e) => e.key === 'Enter' && addDomain()}
                                />
                                <button
                                    onClick={addDomain}
                                    style={{ '--light-control-fill': '#568ded',
                                        padding: '0 16px',
                                        backgroundColor: '#568ded',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#FFFFFF',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                  data-light-control={"filled"}>
                                    {t('manpower.add', 'Add')}
                                </button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {allowedDomains.map((domain, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '5px 10px',
                                            backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.15))',
                                            borderRadius: '14px',
                                            fontSize: '12px',
                                            color: 'var(--app-text, #FFFFFF)'
                                        }}
                                    >
                                        @{domain}
                                        <button
                                            onClick={() => removeDomain(i)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                cursor: 'pointer',
                                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)',
                                                fontSize: '14px',
                                                lineHeight: 1
                                            }}
                                          data-light-control={"quiet"}>
                                            ×
                                        </button>
                                    </span>
                                ))}
                                {allowedDomains.length === 0 && (
                                    <span style={{ fontSize: '12px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>{t('manpower.noDomainsYet', 'No domains added yet')}</span>
                                )}
                            </div>
                        </div>

                        {/* Email Whitelist */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)',
                                marginBottom: '8px',
                                textTransform: 'none',
                                letterSpacing: '0.5px'
                            }}>
                                {t('manpower.emailWhitelist', 'Email Whitelist')} <span style={{ fontWeight: 400, opacity: 0.7 }}>{t('manpower.emailWhitelistSub', '(for external contractors)')}</span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder={t('manpower.emailPlaceholder', 'contractor@external.com')}
                                    className="manpower-settings-input"
                                    style={{
                                        flex: 1,
                                        padding: '10px 12px',
                                        backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))',
                                        border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                                        borderRadius: '8px',
                                        color: 'var(--app-text, #FFFFFF)',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                    onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                                />
                                <button
                                    onClick={addEmail}
                                    style={{ '--light-control-fill': '#568ded',
                                        padding: '0 16px',
                                        backgroundColor: '#568ded',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#FFFFFF',
                                        fontFamily: 'Montserrat, sans-serif',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        height: '38px',
                                        boxSizing: 'border-box'
                                    }}
                                  data-light-control={"filled"}>
                                    {t('manpower.add', 'Add')}
                                </button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {emailWhitelist.map((email, i) => (
                                    <span
                                        key={i}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '5px 10px',
                                            backgroundColor: 'rgba(46, 204, 113, 0.15)',
                                            borderRadius: '14px',
                                            fontSize: '12px',
                                            color: 'var(--app-text, #FFFFFF)'
                                        }}
                                    >
                                        {email}
                                        <button
                                            onClick={() => removeEmail(i)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                cursor: 'pointer',
                                                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)',
                                                fontSize: '14px',
                                                lineHeight: 1
                                            }}
                                          data-light-control={"quiet"}>
                                            ×
                                        </button>
                                    </span>
                                ))}
                                {emailWhitelist.length === 0 && (
                                    <span style={{ fontSize: '12px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>{t('manpower.noEmailsYet', 'No emails added yet')}</span>
                                )}
                            </div>
                        </div>

                        {/* Approver Emails - only show when internal approval is enabled */}
                        {requireInternalApproval && (
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)',
                                    marginBottom: '8px',
                                    textTransform: 'none',
                                    letterSpacing: '0.5px'
                                }}>
                                    {t('manpower.approverEmails', 'Approver Emails')} <span style={{ fontWeight: 400, opacity: 0.7 }}>{t('manpower.approverEmailsSub', '(Finance / GM)')}</span>
                                </label>
                                <p style={{
                                    fontSize: '12px',
                                    color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)',
                                    marginTop: 0,
                                    marginBottom: '12px'
                                }}>
                                    {t('manpower.approverEmailsDesc', 'These people will receive email links to approve or reject requests.')}
                                </p>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                                    <input
                                        type="email"
                                        value={newApprover}
                                        onChange={(e) => setNewApprover(e.target.value)}
                                        placeholder={t('manpower.approverPlaceholder', 'finance@company.com')}
                                        className="manpower-settings-input"
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            backgroundColor: 'var(--app-surface, rgba(255,255,255,0.08))',
                                            border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                                            borderRadius: '8px',
                                            color: 'var(--app-text, #FFFFFF)',
                                            fontFamily: 'Montserrat, sans-serif',
                                            fontSize: '13px',
                                            height: '38px',
                                            boxSizing: 'border-box'
                                        }}
                                        onKeyPress={(e) => e.key === 'Enter' && addApprover()}
                                    />
                                    <button
                                        onClick={addApprover}
                                        style={{ '--light-control-fill': '#568ded',
                                            padding: '0 16px',
                                            backgroundColor: '#568ded',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#FFFFFF',
                                            fontFamily: 'Montserrat, sans-serif',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            height: '38px',
                                            boxSizing: 'border-box'
                                        }}
                                      data-light-control={"filled"}>
                                        {t('manpower.add', 'Add')}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {approverEmails.map((email, i) => (
                                        <span
                                            key={i}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                padding: '5px 10px',
                                                backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                                borderRadius: '14px',
                                                fontSize: '12px',
                                                color: 'var(--app-tone-ffc107, #ffc107)'
                                            }}
                                        >
                                            <i className="fa-solid fa-user-tie" style={{ fontSize: '10px' }} />
                                            {email}
                                            <button
                                                onClick={() => removeApprover(i)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)',
                                                    fontSize: '14px',
                                                    lineHeight: 1
                                                }}
                                              data-light-control={"quiet"}>
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                    {approverEmails.length === 0 && (
                                        <span style={{ fontSize: '12px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.5)' }}>{t('manpower.noApproversYet', 'No approvers added yet')}</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Form Fields Modal */}
            {
                showFieldsModal && (
                    <FormFieldsModal
                        companyUID={companyUID}
                        onClose={() => setShowFieldsModal(false)}
                    />
                )
            }
        </>
    );
};

export default ManpowerRequestSettings;
